import React, {FC, Fragment, useState} from "react";
import useTranslate from "../../modules/core/languages/hooks/useTranslate";
import BasketItem from "../../modules/booking/types/BasketItem";
import NcImage from "../../modules/ui/shared/NcImage/NcImage";
import {StarIcon} from "@heroicons/react/24/solid";
import useDeletePrebookingRoom from "../../modules/booking/hooks/useDeletePrebookingRoom";
import ButtonSecondary from "../../modules/ui/shared/Button/ButtonSecondary";
import moment from "moment/moment";
import useCustomerContext from "../../modules/customers/hooks/useCustomerContext";
import useHotelGetRoomRatesInfo from "../../modules/hotels/hooks/useHotelGetRoomRatesInfo";
import {formatNumber} from "../../modules/common/utils/number";
import useDeleteAllBasketItems from "../../modules/booking/hooks/useDeleteAllBasketItems";
import useBookingSearchContext from "../../modules/booking/hooks/useBookingSearchContext";
import BookingPriceDetailsByNights from "../../modules/booking/components/BookingPriceDetailsByNights";

export interface CheckoutItemProps {
    item: BasketItem;
    itemCount: number;
}

const CheckoutItem: FC<CheckoutItemProps> = ({
    item,
    itemCount
}) => {
    const t = useTranslate();
    const [isOpenModalPriceDetail, setIsOpenModalPriceDetail] = useState(false);
    const {languageCode} = useCustomerContext();

    const bookItemPriceDetailQuery = useHotelGetRoomRatesInfo(
        [item.roomHotelEvent],
        item.arrivalDate,
        item.departureDate
    , {
        execute: isOpenModalPriceDetail && !!item.roomHotelEvent
    });
    const {data: bookPriceDetail, isLoading: isLoadingBookPriceDetail} = bookItemPriceDetailQuery;
    const { save: deleteItem } = useDeletePrebookingRoom();
    const {save: deleteBasketItems } = useDeleteAllBasketItems();
    const { setPrebookingId } = useBookingSearchContext();

    const handleClearBasket = () => {
        deleteBasketItems({
            prebookingId: item.prebookingInternalId
        });
        setPrebookingId(null);
    }
    const closeModal = () => setIsOpenModalPriceDetail(false);

    const handleDeleteItem = () => {
        if (itemCount === 1) {
            handleClearBasket();
        }
        deleteItem({
            prebookingInternalId: item.prebookingInternalId,
            itemId: item.id,
        });
    }

    return (
        <>
            <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="flex-shrink-0 w-full sm:w-40">
                    <div className=" aspect-w-4 aspect-h-3 sm:aspect-h-4 rounded-2xl overflow-hidden">
                        <NcImage src={item.hotelObject.imgContentUrl} />
                    </div>
                </div>
                <div className="sm:px-5 space-y-1 w-[50%]">
                    <div>
                        <span className="text-base font-medium mt-1 block">
                            <div className="flex items-end">
                                {[...Array(item.hotelObject.stars)].map((value, index) => <StarIcon className="w-[18px] h-[18px] text-orange-500" key={index} />)}
                            </div>
                            {item.hotelObject.name}
                        </span>
                    </div>
                    <span className="block  text-sm text-neutral-500 dark:text-neutral-400">
                            {item.trName[languageCode]}
                        </span>
                    <span className="block  text-sm text-neutral-500 dark:text-neutral-400">
                            {t("checkout.from")}: {moment(item.arrivalDate).format('DD/MM/YYYY')}
                    </span>
                    <span className="block  text-sm text-neutral-500 dark:text-neutral-400">
                            {t("checkout.to")}: {moment(item.departureDate).format('DD/MM/YYYY')}
                    </span>
                    <span className="block  text-sm text-neutral-500 dark:text-neutral-400 flex items-center !mt-2">
                        <span>{t("hotelDetail.capacity")}</span>
                        <span className="ml-2">
                            {[...Array(item.adults)].map((value, index) =>
                                <i className="las la-user la-2x" key={index + item.id}></i>
                            )}
                            {[...Array(item.children)].map((value, index) =>
                                <i className="text-2xl las la-user" key={index + item.id}></i>
                            )}
                        </span>
                    </span>
                </div>
                <div className="py-5 sm:px-5 space-y-3 w-4/6">
                    <div className="flex flex-col space-y-4">
                        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                            <span>{t("checkout.total_nights")}</span>
                            <span>{item.totalNights} {t("checkout.nights")}</span>
                        </div>
                        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                            <span>{t("checkout.total_without_taxes")}</span>
                            <div>{formatNumber(item.priceWithoutTaxes)} <span dangerouslySetInnerHTML={{ __html:item.currencySymbol}} /></div>
                        </div>
                        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                            <span>{t("checkout.total_taxes")} ({item.taxRate}%)</span>
                            <div>{formatNumber(item.taxes)} <span dangerouslySetInnerHTML={{ __html:item.currencySymbol}} /></div>
                        </div>

                        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
                        <div className="flex justify-between font-semibold">
                            <div className="flex flex-row">
                                <span>{t("checkout.total_to_pay")}</span>
                                <button
                                    onClick={() => setIsOpenModalPriceDetail(true)}
                                    className={`items-center p-2 -m-3 text-left pl-5`}
                                ><i className={"text-3xl las la-question-circle"}></i>
                                </button>
                            </div>
                            <div>{formatNumber(item.priceWithTaxes)} <span dangerouslySetInnerHTML={{ __html:item.currencySymbol}} /></div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <ButtonSecondary onClick={handleDeleteItem} className="float-right"><i className={"text-3xl las la-trash-alt"}></i> {t("checkout.delete")}</ButtonSecondary>
            </div>
            <BookingPriceDetailsByNights
              bookPriceDetail={bookPriceDetail}
              totalPrice={item.priceWithTaxes}
              currencySymbol={item.currencySymbol}
              isLoadingBookPriceDetail={isLoadingBookPriceDetail}
              openModal={isOpenModalPriceDetail}
              onClose={closeModal}
            />
        </>
    );
}

export default CheckoutItem;
